/* Brevo Wishlist Email helper — posts to the server-side proxy.
 *
 * Sends POST {proxyUrl}/wishlist-email with { toEmail, wishlistUrl,
 * customerName, templateId }. The proxy holds the Brevo API key and forwards
 * to POST /v3/smtp/email, which sends a Brevo-hosted template (sender,
 * subject, and HTML body live in Brevo) with `params.wishlistUrl` and
 * `params.customerName` injected as template variables.
 *
 * Docs: https://developers.brevo.com/reference/sendtransacemail
 *
 * The Brevo API key is NEVER exposed here. Storefront JS is public, so the
 * key stays server-side behind the proxy; this file only knows the proxy URL
 * (settings.brevo_proxy_url, rendered in layout/theme.liquid).
 *
 * NOTE: the proxy must rate-limit per IP and validate the Origin header.
 * Without that, this endpoint is an open relay for sending your template to
 * arbitrary recipients — the same abuse the exposed key allowed, just moved
 * one hop. Ideally the proxy also ignores a client-supplied templateId and
 * pins its own.
 *
 * Usage:
 *   window.bodiskinBrevoWishlistEmail.send({
 *     toEmail, wishlistUrl,   // both required
 *     customerName            // optional, defaults to empty string
 *   });
 * Resolves to { ok: boolean, status: number } — status is the HTTP status,
 * or 0 for a network failure / client-side validation abort. Callers must
 * branch on `.ok`, never on the object itself (it is always truthy). */
(function () {
  var ENDPOINT_PATH = '/wishlist-email';
  var LOG = '[BrevoWishlistEmail]';

  /* Mirrors the proxy's server-side validation so bad input fails fast here
   * instead of costing a round trip. The server is still authoritative. */
  var MAX_EMAIL_LEN = 254;
  var MAX_NAME_LEN = 200;
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  console.log(LOG, 'helper script loaded');
  console.log(LOG, 'window.bodiskinBrevoConfig =', window.bodiskinBrevoConfig);

  function getConfig() {
    return window.bodiskinBrevoConfig || {};
  }

  function fail(status) {
    console.groupEnd();
    return Promise.resolve({ ok: false, status: status || 0 });
  }

  /* The proxy rejects any wishlistUrl that is not absolute https on the shop
   * domain — it refuses to email arbitrary links under our branding. Check the
   * same thing here so a misbuilt URL surfaces as a console warning rather
   * than an opaque 400. */
  function isAllowedWishlistUrl(raw) {
    var parsed;
    try {
      parsed = new URL(raw);
    } catch (err) {
      return false;
    }
    if (parsed.protocol !== 'https:') return false;
    var host = parsed.hostname.toLowerCase();
    return (
      host === 'bodiskin.com' ||
      host === 'www.bodiskin.com' ||
      /\.myshopify\.com$/.test(host)
    );
  }

  function describeFailure(status) {
    if (status === 400) return 'Invalid or missing field, or wishlistUrl is off-domain.';
    if (status === 401 || status === 403) return 'Proxy rejected the request (origin not allowed).';
    if (status === 402) return 'Brevo plan quota exceeded.';
    if (status === 404) return 'Proxy route missing, or template not found upstream.';
    if (status === 429) return 'Rate limited — per IP, and max 3/hour per recipient.';
    if (status === 500 || status === 502) return 'Proxy misconfigured, or Brevo unreachable.';
    return 'Unexpected error.';
  }

  function send(payload) {
    console.group(LOG, 'send() called');
    console.log('payload:', payload);

    payload = payload || {};
    var toEmail = (payload.toEmail || '').trim();
    var wishlistUrl = (payload.wishlistUrl || '').trim();
    var customerName = (payload.customerName || '').toString();

    if (!toEmail) {
      console.warn(LOG, 'no toEmail — aborting');
      return fail(0);
    }
    if (toEmail.length > MAX_EMAIL_LEN || !EMAIL_RE.test(toEmail)) {
      console.warn(LOG, 'toEmail failed validation — aborting');
      return fail(0);
    }
    if (!wishlistUrl) {
      console.warn(LOG, 'no wishlistUrl — aborting');
      return fail(0);
    }
    if (!isAllowedWishlistUrl(wishlistUrl)) {
      console.warn(LOG, 'wishlistUrl is not absolute https on the shop domain — aborting:', wishlistUrl);
      return fail(0);
    }
    if (customerName.length > MAX_NAME_LEN) {
      customerName = customerName.slice(0, MAX_NAME_LEN);
    }

    var config = getConfig();
    console.log(LOG, 'config snapshot:', { proxyUrl: config.proxyUrl || '(empty)' });

    if (!config.proxyUrl) {
      console.warn(LOG, 'proxyUrl missing in window.bodiskinBrevoConfig — aborting');
      return fail(0);
    }

    /* No templateId is sent: the proxy pins the Brevo template server-side so
     * a caller cannot make it send some other template. */
    var url = config.proxyUrl.replace(/\/+$/, '') + ENDPOINT_PATH;
    var body = {
      toEmail: toEmail,
      wishlistUrl: wishlistUrl,
      customerName: customerName,
    };

    console.log(LOG, 'POST', url);
    console.log(LOG, 'request body:', body);
    console.groupEnd();

    return fetch(url, {
      method: 'POST',
      headers: {
        'accept': 'application/json',
        'content-type': 'application/json',
      },
      body: JSON.stringify(body),
    })
      .then(function (res) {
        console.log(LOG, 'response status:', res.status, res.statusText);
        if (res.ok) {
          console.log(LOG, '✓ email queued (status', res.status + ')');
          return res.json().catch(function () { return null; }).then(function (data) {
            if (data) console.log(LOG, 'response body:', data);
            return { ok: true, status: res.status };
          });
        }
        return res
          .json()
          .catch(function () { return { error: '(no body)' }; })
          .then(function (err) {
            console.warn(LOG, '✗ proxy ' + res.status + ':', describeFailure(res.status));
            console.warn(LOG, 'error body:', err);
            return { ok: false, status: res.status };
          });
      })
      .catch(function (err) {
        console.warn(LOG, 'fetch threw (network/CORS):', err);
        return { ok: false, status: 0 };
      });
  }

  window.bodiskinBrevoWishlistEmail = { send: send };
  console.log(LOG, 'window.bodiskinBrevoWishlistEmail.send is ready');
})();
